import { AuthGuard } from '@/components/auth/auth-guard';
import { AppLayout } from '@/components/layout';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { Button } from '@/components/ui/button';
import { DatePicker } from '@/components/ui/date-picker';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Skeleton } from '@/components/ui/skeleton';
import { useCreateAccessGrant, useInventoryDeleteAccessGrant, useInventoryRevokeAccessGrant, useInventoryUpdateAccessGrant } from '@/hooks/use-access-grants';
import { useAccessTypes } from '@/hooks/use-access-types';
import { useAssetTypes } from '@/hooks/use-asset-types';
import { useCreateAsset, useInventory, useInventoryDeleteAsset, useInventoryReturnAsset } from '@/hooks/use-assets';
import { useEmployeeNameFormatter } from '@/lib/employee-name';
import { cn } from '@/lib/utils';
import { hasPermission, useCanReadAssetTypes, usePermission } from '@/lib/permissions';
import { extractErrorMessage } from '@/lib/errors';
import { useAuthStore } from '@/stores/auth';
import type { InventoryEmployee } from '@hygge/shared';
import { createFileRoute, Link, redirect, useNavigate } from '@tanstack/react-router';
import { ArrowDown, ArrowUp, ArrowUpDown, Check, Filter, Plus, RotateCcw, Search, ShieldOff, Trash2, X } from 'lucide-react';
import { useCallback, useMemo, useState } from 'react';

type InventoryTab = 'assets' | 'access';
type SortDirection = 'asc' | 'desc';
type SortConfig = { key: string; direction: SortDirection } | null;

type StatusFilter = 'current_staff' | 'terminated' | 'all';

const STATUS_FILTER_OPTIONS: { value: StatusFilter; label: string }[] = [
  { value: 'current_staff', label: 'Current Staff' },
  { value: 'terminated', label: 'Offboarded' },
  { value: 'all', label: 'All' },
];

const DEFAULT_STATUS_FILTER: StatusFilter = 'current_staff';

function statusFilterToParams(filter: StatusFilter): Record<string, string> | undefined {
  if (filter === 'current_staff') return { statuses: 'active,probation,onboarding' };
  if (filter === 'terminated') return { statuses: 'terminated,inactive' };
  return undefined;
}

const routePath = '/inventory' as const;

export const Route = createFileRoute(routePath)({
  beforeLoad: () => {
    // Mirror recruitment.vacancies: sync gate on the hot path, await /auth/me
    // on a cold direct hit so the asset table never flashes for a viewer
    // without `assets.view`.
    const checkGate = () => {
      const { isAuthenticated, user } = useAuthStore.getState();
      if (!isAuthenticated) throw redirect({ to: '/login' });
      if (!hasPermission(user, 'assets.view')) throw redirect({ to: '/people' });
    };
    if (useAuthStore.getState().isInitialized) {
      checkGate();
      return;
    }
    return useAuthStore.getState().initialize().then(checkGate);
  },
  component: InventoryPage,
});

function getInitials(firstName: string, lastName: string) {
  return `${firstName?.charAt(0) || ''}${lastName?.charAt(0) || ''}`.toUpperCase();
}

function InventoryPage() {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState<InventoryTab>('assets');
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState<StatusFilter>(DEFAULT_STATUS_FILTER);
  const [assetsSort, setAssetsSort] = useState<SortConfig>(null);
  const [accessSort, setAccessSort] = useState<SortConfig>(null);
  const formatName = useEmployeeNameFormatter();

  const inventoryParams = useMemo(() => statusFilterToParams(statusFilter), [statusFilter]);
  const { data: inventory, isLoading: isLoadingInventory } = useInventory(inventoryParams);
  // The inventory answers with access grants only for a holder of `access.view`
  // [HW-942], so without it the Access table would be all zeroes: it is not
  // offered. A type catalog is read only by a holder of one of its module's
  // codes, which its route requires since HW-942 — the request could only 403.
  const canViewAccess = usePermission('access.view');
  const canReadAssetTypes = useCanReadAssetTypes();
  const shownTab: InventoryTab = canViewAccess ? activeTab : 'assets';
  const { data: assetTypes, isLoading: isLoadingAssetTypes } = useAssetTypes(false, {
    enabled: canReadAssetTypes,
  });
  const { data: accessTypes, isLoading: isLoadingAccessTypes } = useAccessTypes(false, {
    // Only the Access table reads the access types, and `access.view` (which
    // opens that table) is one of the catalog's read codes.
    enabled: canViewAccess,
  });

  const isLoading = isLoadingInventory || isLoadingAssetTypes || isLoadingAccessTypes;

  // Filter employees by search term
  const filteredEmployees = useMemo(() => {
    if (!inventory) return [];
    if (!search.trim()) return inventory;
    const term = search.trim().toLowerCase();
    return inventory.filter(
      (emp) =>
        // Formatted (displayed) name plus raw first-last covers both orders and any single-part substring.
        formatName({ firstName: emp.firstName, lastName: emp.lastName }).toLowerCase().includes(term) ||
        `${emp.firstName} ${emp.lastName}`.toLowerCase().includes(term),
    );
  }, [inventory, search, formatName]);

  return (
    <AuthGuard>
      <AppLayout>
        {/* Page Header */}
        <header className="sticky top-14 z-30 flex h-12 items-center border-b border-border bg-background/95 px-6 backdrop-blur supports-[backdrop-filter]:bg-background/60">
          <div className="flex items-center gap-2 text-sm text-muted-foreground">
            <span>Home</span>
            <span>/</span>
            <span className="font-medium text-foreground">Inventory</span>
          </div>
        </header>

        {/* Main content */}
        <div className="p-6">
          {/* Page title */}
          <div className="mb-1">
            <h1 className="text-xl font-semibold tracking-tight text-foreground">
              Assets & Access Inventory
            </h1>
            <p className="mt-1 text-sm text-muted-foreground">
              Overview of all employee assets and system access
            </p>
          </div>

          {/* Controls bar */}
          <div className="mb-4 mt-5 flex items-center gap-3">
            {/* Tab switcher */}
            <div className="inline-flex rounded-lg border border-border p-1">
              <button
                type="button"
                className={cn(
                  'cursor-pointer rounded-md px-3 py-1.5 text-xs font-medium transition-colors',
                  shownTab === 'assets'
                    ? 'bg-cta text-cta-foreground'
                    : 'text-muted-foreground hover:text-foreground',
                )}
                onClick={() => setActiveTab('assets')}
              >
                Assets
              </button>
              {canViewAccess && (
                <button
                  type="button"
                  className={cn(
                    'cursor-pointer rounded-md px-3 py-1.5 text-xs font-medium transition-colors',
                    shownTab === 'access'
                      ? 'bg-cta text-cta-foreground'
                      : 'text-muted-foreground hover:text-foreground',
                  )}
                  onClick={() => setActiveTab('access')}
                >
                  Access
                </button>
              )}
            </div>

            {/* Search */}
            <div className="relative max-w-sm flex-1">
              <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
              <Input
                type="text"
                placeholder="Search by name..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className="pl-9 pr-9"
              />
              {search && (
                <button
                  type="button"
                  aria-label="Clear search"
                  onClick={() => setSearch('')}
                  className="absolute right-3 top-1/2 -translate-y-1/2 cursor-pointer text-muted-foreground hover:text-foreground"
                >
                  <X className="h-4 w-4" />
                </button>
              )}
            </div>

            {/* Status filter */}
            <div className="ml-auto">
              <StatusFilterMenu value={statusFilter} onChange={setStatusFilter} />
            </div>
          </div>

          {/* Table */}
          {isLoading ? (
            <SkeletonTable />
          ) : filteredEmployees.length === 0 ? (
            <EmptyState />
          ) : shownTab === 'assets' ? (
            <AssetsTable
              employees={filteredEmployees}
              assetTypes={assetTypes ?? []}
              sort={assetsSort}
              onSort={setAssetsSort}
              onRowClick={(id) => navigate({ to: '/employees/$id', params: { id } })}
            />
          ) : (
            <AccessTable
              employees={filteredEmployees}
              accessTypes={accessTypes ?? []}
              sort={accessSort}
              onSort={setAccessSort}
              onRowClick={(id) => navigate({ to: '/employees/$id', params: { id } })}
            />
          )}
        </div>
      </AppLayout>
    </AuthGuard>
  );
}

function StatusFilterMenu({
  value,
  onChange,
}: {
  value: StatusFilter;
  onChange: (next: StatusFilter) => void;
}) {
  const activeLabel = STATUS_FILTER_OPTIONS.find((o) => o.value === value)?.label ?? '';
  const isDefault = value === DEFAULT_STATUS_FILTER;
  return (
    <div className="inline-flex h-8 items-center overflow-hidden rounded-md border border-border bg-background shadow-xs dark:border-input dark:bg-input/30">
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <button
            type="button"
            className={cn(
              'inline-flex h-full cursor-pointer items-center gap-2 pl-3 text-xs font-medium outline-none transition-colors hover:bg-accent hover:text-accent-foreground focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-ring dark:hover:bg-input/50',
              isDefault ? 'pr-3' : 'pr-1.5',
            )}
          >
            <Filter className="h-3.5 w-3.5" />
            <span>{activeLabel}</span>
            {!isDefault && (
              <span className="inline-flex h-1.5 w-1.5 rounded-full bg-primary" aria-hidden />
            )}
          </button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end" className="w-44">
          {STATUS_FILTER_OPTIONS.map((option) => (
            <DropdownMenuItem
              key={option.value}
              onClick={() => onChange(option.value)}
              className="flex items-center justify-between gap-2"
            >
              <span>{option.label}</span>
              {option.value === value && <Check className="h-3.5 w-3.5 text-primary" />}
            </DropdownMenuItem>
          ))}
        </DropdownMenuContent>
      </DropdownMenu>
      {!isDefault && (
        <button
          type="button"
          aria-label="Reset filter"
          title="Reset filter"
          onClick={() => onChange(DEFAULT_STATUS_FILTER)}
          className="inline-flex h-full cursor-pointer items-center pl-0.5 pr-2.5 text-muted-foreground outline-none transition-colors hover:bg-accent hover:text-foreground focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-ring dark:hover:bg-input/50"
        >
          <X className="h-3.5 w-3.5" />
        </button>
      )}
    </div>
  );
}

function SortIcon({ columnKey, sort }: { columnKey: string; sort: SortConfig }) {
  if (sort?.key !== columnKey) {
    return <ArrowUpDown className="h-3 w-3 opacity-0 transition-opacity group-hover:opacity-100" />;
  }
  return sort.direction === 'asc' ? <ArrowUp className="h-3 w-3" /> : <ArrowDown className="h-3 w-3" />;
}

type InventoryAsset = InventoryEmployee['assets'][number];

function toDateOnly(date: Date): string {
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, '0');
  const d = String(date.getDate()).padStart(2, '0');
  return `${y}-${m}-${d}`;
}

function AssetPopover({
  asset,
  employeeName,
}: {
  asset: InventoryAsset;
  employeeName: string;
}) {
  const [open, setOpen] = useState(false);
  const [showReturnConfirm, setShowReturnConfirm] = useState(false);
  const [returnDate, setReturnDate] = useState<Date | null>(new Date());
  const [actionError, setActionError] = useState<string | null>(null);
  const canManage = usePermission('assets.manage');
  const returnAsset = useInventoryReturnAsset();
  const deleteAsset = useInventoryDeleteAsset();

  const handleReturn = async (e: React.MouseEvent) => {
    e.stopPropagation();
    if (!returnDate) return;
    setActionError(null);
    try {
      await returnAsset.mutateAsync({ id: asset.id, returnedAt: toDateOnly(returnDate) });
      setShowReturnConfirm(false);
      setOpen(false);
    } catch (err) {
      setActionError(extractErrorMessage(err, 'Failed to return asset'));
    }
  };

  const handleDelete = async (e: React.MouseEvent) => {
    e.stopPropagation();
    setActionError(null);
    try {
      await deleteAsset.mutateAsync(asset.id);
      setOpen(false);
    } catch (err) {
      setActionError(extractErrorMessage(err, 'Failed to delete asset'));
    }
  };

  return (
    <Popover open={open} onOpenChange={(v) => { setOpen(v); if (!v) setShowReturnConfirm(false); }}>
      <PopoverTrigger asChild>
        <button
          type="button"
          className="group/asset block min-w-0 flex-1 cursor-pointer text-left text-foreground"
          onClick={(e) => e.stopPropagation()}
        >
          <span className="block truncate group-hover/asset:text-primary group-hover/asset:underline">
            {asset.name}
          </span>
          {asset.identifier && (
            <span className="block truncate font-mono text-[11px] text-muted-foreground">
              {asset.identifier}
            </span>
          )}
        </button>
      </PopoverTrigger>
      <PopoverContent align="start" className="w-64 p-3" onClick={(e) => e.stopPropagation()}>
        <div className="space-y-3">
          <div>
            <p className="text-sm font-medium text-foreground">{asset.name}</p>
            {asset.identifier && (
              <p className="text-xs text-muted-foreground">{asset.identifier}</p>
            )}
            <p className="mt-1 text-xs text-muted-foreground">
              Assigned to {employeeName}
            </p>
          </div>
          {actionError && (
            <p className="text-xs text-destructive">{actionError}</p>
          )}
          {canManage && (showReturnConfirm ? (
            <div className="space-y-2">
              <Label className="text-xs">Return Date</Label>
              <DatePicker
                value={returnDate}
                onChange={setReturnDate}
                placeholder="Select date"
              />
              <div className="flex gap-2 pt-1">
                <Button
                  size="sm"
                  variant="outline"
                  className="h-7 flex-1 text-xs"
                  onClick={(e) => { e.stopPropagation(); setShowReturnConfirm(false); }}
                >
                  Back
                </Button>
                <Button
                  size="sm"
                  className="h-7 flex-1 text-xs"
                  onClick={handleReturn}
                  disabled={!returnDate || returnAsset.isPending}
                >
                  {returnAsset.isPending ? 'Returning...' : 'Confirm'}
                </Button>
              </div>
            </div>
          ) : (
            <div className="flex gap-2">
              <Button
                size="sm"
                variant="outline"
                className="h-7 flex-1 text-xs"
                onClick={(e) => { e.stopPropagation(); setReturnDate(new Date()); setShowReturnConfirm(true); }}
              >
                <RotateCcw className="mr-1 h-3 w-3" />
                Return
              </Button>
              <Button
                size="sm"
                variant="outline"
                className="h-7 flex-1 text-xs text-destructive hover:bg-destructive hover:text-destructive-foreground"
                onClick={handleDelete}
                disabled={deleteAsset.isPending}
              >
                <Trash2 className="mr-1 h-3 w-3" />
                {deleteAsset.isPending ? 'Deleting...' : 'Delete'}
              </Button>
            </div>
          ))}
        </div>
      </PopoverContent>
    </Popover>
  );
}

type InventoryAccessGrant = InventoryEmployee['accessGrants'][number];

function AccessGrantPopover({
  grant,
  typeName,
  employeeName,
}: {
  grant: InventoryAccessGrant;
  typeName: string;
  employeeName: string;
}) {
  const [open, setOpen] = useState(false);
  const [editRole, setEditRole] = useState('');
  const [isEditing, setIsEditing] = useState(false);
  const [actionError, setActionError] = useState<string | null>(null);
  const canManage = usePermission('access.manage');
  const updateGrant = useInventoryUpdateAccessGrant();
  const revokeGrant = useInventoryRevokeAccessGrant();
  const deleteGrant = useInventoryDeleteAccessGrant();

  const handleOpen = (v: boolean) => {
    if (v) {
      setEditRole(grant.role || '');
      setIsEditing(false);
      setActionError(null);
    }
    setOpen(v);
  };

  const handleSaveRole = async (e: React.MouseEvent) => {
    e.stopPropagation();
    const newRole = editRole.trim() || null;
    if (newRole === (grant.role || null)) {
      setIsEditing(false);
      return;
    }
    setActionError(null);
    try {
      await updateGrant.mutateAsync({ id: grant.id, role: newRole });
      setIsEditing(false);
      setOpen(false);
    } catch (err) {
      setActionError(extractErrorMessage(err, 'Failed to update role'));
    }
  };

  const handleRevoke = async (e: React.MouseEvent) => {
    e.stopPropagation();
    setActionError(null);
    try {
      await revokeGrant.mutateAsync(grant.id);
      setOpen(false);
    } catch (err) {
      setActionError(extractErrorMessage(err, 'Failed to revoke access'));
    }
  };

  const handleDelete = async (e: React.MouseEvent) => {
    e.stopPropagation();
    setActionError(null);
    try {
      await deleteGrant.mutateAsync(grant.id);
      setOpen(false);
    } catch (err) {
      setActionError(extractErrorMessage(err, 'Failed to delete grant'));
    }
  };

  return (
    <Popover open={open} onOpenChange={handleOpen}>
      <PopoverTrigger asChild>
        <button
          type="button"
          className="cursor-pointer"
          onClick={(e) => e.stopPropagation()}
        >
          <span className="inline-flex items-center gap-1">
            {grant.role ? (
              <span className="inline-flex items-center rounded-md bg-primary/10 px-1.5 py-0.5 text-xs font-medium text-primary hover:bg-primary/20 transition-colors">
                {grant.role}
              </span>
            ) : (
              <span className="inline-flex items-center rounded-md bg-emerald-500/10 px-1.5 py-0.5 text-xs font-medium text-emerald-600 hover:bg-emerald-500/20 transition-colors">
                Granted
              </span>
            )}
            {grant.identifier && (
              <span className="text-xs text-muted-foreground">{grant.identifier}</span>
            )}
          </span>
        </button>
      </PopoverTrigger>
      <PopoverContent align="start" className="w-64 p-3" onClick={(e) => e.stopPropagation()}>
        <div className="space-y-3">
          <div>
            <p className="text-sm font-medium text-foreground">{typeName}</p>
            {grant.identifier && (
              <p className="text-xs text-muted-foreground">{grant.identifier}</p>
            )}
            <p className="mt-0.5 text-xs text-muted-foreground">
              Granted to {employeeName}
            </p>
          </div>
          {actionError && (
            <p className="text-xs text-destructive">{actionError}</p>
          )}
          {canManage && (isEditing ? (
            <div className="space-y-2">
              <Input
                placeholder="Role, e.g. Admin, Member"
                value={editRole}
                onChange={(e) => setEditRole(e.target.value)}
                className="h-8 text-xs"
                autoFocus
                onKeyDown={(e) => { if (e.key === 'Enter') handleSaveRole(e as any); if (e.key === 'Escape') setIsEditing(false); }}
              />
              <div className="flex gap-2">
                <Button size="sm" variant="outline" className="h-7 flex-1 text-xs" onClick={() => setIsEditing(false)}>
                  Cancel
                </Button>
                <Button size="sm" className="h-7 flex-1 text-xs" onClick={handleSaveRole} disabled={updateGrant.isPending}>
                  {updateGrant.isPending ? 'Saving...' : 'Save'}
                </Button>
              </div>
            </div>
          ) : (
            <>
              <Button
                size="sm"
                variant="outline"
                className="h-7 w-full text-xs"
                onClick={(e) => { e.stopPropagation(); setIsEditing(true); }}
              >
                Change role{grant.role ? `: ${grant.role}` : ''}
              </Button>
              <div className="flex gap-2">
                <Button
                  size="sm"
                  variant="outline"
                  className="h-7 flex-1 text-xs"
                  onClick={handleRevoke}
                  disabled={revokeGrant.isPending}
                >
                  <ShieldOff className="mr-1 h-3 w-3" />
                  {revokeGrant.isPending ? 'Revoking...' : 'Revoke'}
                </Button>
                <Button
                  size="sm"
                  variant="outline"
                  className="h-7 flex-1 text-xs text-destructive hover:bg-destructive hover:text-destructive-foreground"
                  onClick={handleDelete}
                  disabled={deleteGrant.isPending}
                >
                  <Trash2 className="mr-1 h-3 w-3" />
                  {deleteGrant.isPending ? 'Deleting...' : 'Delete'}
                </Button>
              </div>
            </>
          ))}
        </div>
      </PopoverContent>
    </Popover>
  );
}

function AddAssetPopover({
  employeeId,
  assetTypeId,
  assetTypeName,
  alwaysVisible,
}: {
  employeeId: string;
  assetTypeId: string;
  assetTypeName: string;
  alwaysVisible?: boolean;
}) {
  const [open, setOpen] = useState(false);
  const [name, setName] = useState('');
  const [identifier, setIdentifier] = useState('');
  const [assignedAt, setAssignedAt] = useState<Date | null>(new Date());
  const [notes, setNotes] = useState('');
  const [submitError, setSubmitError] = useState<string | null>(null);
  const canManage = usePermission('assets.manage');
  const createAsset = useCreateAsset({ suppressGlobalToast: true });

  const reset = () => { setName(''); setIdentifier(''); setAssignedAt(new Date()); setNotes(''); setSubmitError(null); };

  const handleSubmit = async (e: React.MouseEvent) => {
    e.stopPropagation();
    if (!name.trim()) return;
    setSubmitError(null);
    try {
      await createAsset.mutateAsync({
        assetTypeId,
        employeeId,
        name: name.trim(),
        identifier: identifier.trim() || undefined,
        assignedAt: assignedAt ? assignedAt.toISOString() : undefined,
        notes: notes.trim() || undefined,
      });
      reset();
      setOpen(false);
    } catch (err) {
      setSubmitError(extractErrorMessage(err, 'Failed to assign asset'));
    }
  };

  if (!canManage) return null;

  return (
    <Popover open={open} onOpenChange={(v) => { if (!v) reset(); setOpen(v); }}>
      <PopoverTrigger asChild>
        <button
          type="button"
          className={cn(
            'inline-flex h-6 w-6 shrink-0 items-center justify-center rounded-full border border-border text-muted-foreground transition-all hover:bg-muted hover:text-foreground',
            !alwaysVisible && 'opacity-0 group-hover/cell:opacity-100',
          )}
          onClick={(e) => e.stopPropagation()}
          title={`Add ${assetTypeName}`}
        >
          <Plus className="h-3 w-3" />
        </button>
      </PopoverTrigger>
      <PopoverContent align="start" className="w-72 p-3" onClick={(e) => e.stopPropagation()}>
        <div className="space-y-3">
          <p className="text-xs font-medium text-muted-foreground">
            Assign {assetTypeName}
          </p>
          <div className="space-y-2">
            <Input
              placeholder="Name, e.g. MacBook Pro 16&quot;"
              value={name}
              onChange={(e) => setName(e.target.value)}
              className="h-8 text-xs"
              autoFocus
            />
            <Input
              placeholder="Identifier (optional)"
              value={identifier}
              onChange={(e) => setIdentifier(e.target.value)}
              className="h-8 text-xs"
            />
            <DatePicker
              value={assignedAt}
              onChange={setAssignedAt}
              placeholder="Select date"
            />
            <textarea
              placeholder="Notes (optional)"
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              rows={2}
              className="w-full rounded-md border border-input bg-background px-3 py-1.5 text-xs ring-offset-background placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
            />
          </div>
          {submitError && (
            <p className="text-xs text-destructive">{submitError}</p>
          )}
          <Button
            size="sm"
            className="h-7 w-full text-xs"
            onClick={handleSubmit}
            disabled={!name.trim() || createAsset.isPending}
          >
            {createAsset.isPending ? 'Assigning...' : 'Assign'}
          </Button>
        </div>
      </PopoverContent>
    </Popover>
  );
}

function AddAccessGrantPopover({
  employeeId,
  accessTypeId,
  accessTypeName,
  defaultEmail,
  alwaysVisible,
}: {
  employeeId: string;
  accessTypeId: string;
  accessTypeName: string;
  defaultEmail: string;
  alwaysVisible?: boolean;
}) {
  const [open, setOpen] = useState(false);
  const [accountEmail, setAccountEmail] = useState(defaultEmail);
  const [identifier, setIdentifier] = useState('');
  const [role, setRole] = useState('');
  const [grantedAt, setGrantedAt] = useState<Date | null>(new Date());
  const [expiresAt, setExpiresAt] = useState<Date | null>(null);
  const [notes, setNotes] = useState('');
  const [submitError, setSubmitError] = useState<string | null>(null);
  const canManage = usePermission('access.manage');
  const createGrant = useCreateAccessGrant({ suppressGlobalToast: true });

  const reset = () => { setAccountEmail(defaultEmail); setIdentifier(''); setRole(''); setGrantedAt(new Date()); setExpiresAt(null); setNotes(''); setSubmitError(null); };

  const handleSubmit = async (e: React.MouseEvent) => {
    e.stopPropagation();
    setSubmitError(null);
    try {
      await createGrant.mutateAsync({
        accessTypeId,
        employeeId,
        accountEmail: accountEmail.trim() || undefined,
        identifier: identifier.trim() || undefined,
        role: role.trim(),
        grantedAt: grantedAt ? grantedAt.toISOString() : undefined,
        expiresAt: expiresAt ? expiresAt.toISOString() : undefined,
        notes: notes.trim() || undefined,
      });
      reset();
      setOpen(false);
    } catch (err) {
      setSubmitError(extractErrorMessage(err, 'Failed to grant access'));
    }
  };

  if (!canManage) return null;

  return (
    <Popover open={open} onOpenChange={(v) => { if (!v) reset(); setOpen(v); }}>
      <PopoverTrigger asChild>
        <button
          type="button"
          className={cn(
            'inline-flex h-6 w-6 shrink-0 items-center justify-center rounded-full border border-border text-muted-foreground transition-all hover:bg-muted hover:text-foreground',
            !alwaysVisible && 'opacity-0 group-hover/cell:opacity-100',
          )}
          onClick={(e) => e.stopPropagation()}
          title={`Grant ${accessTypeName}`}
        >
          <Plus className="h-3 w-3" />
        </button>
      </PopoverTrigger>
      <PopoverContent align="start" className="w-72 p-3" onClick={(e) => e.stopPropagation()}>
        <div className="space-y-3">
          <p className="text-xs font-medium text-muted-foreground">
            Grant {accessTypeName}
          </p>
          <div className="space-y-2">
            <Input
              type="email"
              placeholder="Account email (optional)"
              value={accountEmail}
              onChange={(e) => setAccountEmail(e.target.value)}
              className="h-8 text-xs"
              autoFocus
            />
            <Input
              placeholder="Identifier, e.g. Project Alpha (optional)"
              value={identifier}
              onChange={(e) => setIdentifier(e.target.value)}
              className="h-8 text-xs"
            />
            <Input
              placeholder="Role, e.g. Admin, Member"
              required
              value={role}
              onChange={(e) => setRole(e.target.value)}
              className="h-8 text-xs"
            />
            <DatePicker
              value={grantedAt}
              onChange={setGrantedAt}
              placeholder="Granted date"
            />
            <DatePicker
              value={expiresAt}
              onChange={setExpiresAt}
              placeholder="Expiry date (optional)"
            />
            <textarea
              placeholder="Notes (optional)"
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              rows={2}
              className="w-full rounded-md border border-input bg-background px-3 py-1.5 text-xs ring-offset-background placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
            />
          </div>
          {submitError && (
            <p className="text-xs text-destructive">{submitError}</p>
          )}
          <Button
            size="sm"
            className="h-7 w-full text-xs"
            onClick={handleSubmit}
            disabled={!role.trim() || createGrant.isPending}
          >
            {createGrant.isPending ? 'Granting...' : 'Grant'}
          </Button>
        </div>
      </PopoverContent>
    </Popover>
  );
}

function useToggleSort(sort: SortConfig, onSort: (s: SortConfig) => void) {
  return useCallback(
    (key: string) => {
      if (sort?.key !== key) {
        onSort({ key, direction: 'asc' });
      } else if (sort.direction === 'asc') {
        onSort({ key, direction: 'desc' });
      } else {
        onSort(null);
      }
    },
    [sort, onSort],
  );
}

const sortableThBase =
  'px-4 py-3 text-left text-[11px] font-semibold uppercase tracking-wide cursor-pointer select-none group transition-colors';
const sortableThInactive = `${sortableThBase} text-muted-foreground hover:text-foreground`;
const sortableThActive = `${sortableThBase} text-primary`;

function AssetsTable({
  employees,
  assetTypes,
  sort,
  onSort,
  onRowClick,
}: {
  employees: InventoryEmployee[];
  assetTypes: { id: string; name: string; icon: string | null }[];
  sort: SortConfig;
  onSort: (s: SortConfig) => void;
  onRowClick: (id: string) => void;
}) {
  const formatName = useEmployeeNameFormatter();
  const toggleSort = useToggleSort(sort, onSort);

  // Count total items per type across all employees
  const typeCounts = useMemo(() => {
    const counts = new Map<string, number>();
    for (const emp of employees) {
      for (const asset of emp.assets) {
        counts.set(asset.assetType.id, (counts.get(asset.assetType.id) || 0) + 1);
      }
    }
    return counts;
  }, [employees]);

  const sortedEmployees = useMemo(() => {
    if (!sort) return employees;
    const dir = sort.direction === 'asc' ? 1 : -1;
    const nameKey = (e: InventoryEmployee) =>
      formatName({ firstName: e.firstName, lastName: e.lastName }).toLowerCase();

    return [...employees].sort((a, b) => {
      if (sort.key === 'employee') {
        return nameKey(a).localeCompare(nameKey(b)) * dir;
      }
      if (sort.key === 'total') {
        return (a.assets.length - b.assets.length) * dir;
      }
      const countA = a.assets.filter((asset) => asset.assetType.id === sort.key).length;
      const countB = b.assets.filter((asset) => asset.assetType.id === sort.key).length;
      if (countA !== countB) return (countA - countB) * dir;
      // Secondary sort by name for stable order (always ascending)
      return nameKey(a).localeCompare(nameKey(b));
    });
  }, [employees, sort, formatName]);

  return (
    <div className="overflow-hidden rounded-xl border border-border bg-card">
      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b border-border/50 bg-muted/30">
              <th className={sort?.key === 'employee' ? sortableThActive : sortableThInactive} onClick={() => toggleSort('employee')}>
                <div className="flex items-center gap-1">
                  Employee
                  <SortIcon columnKey="employee" sort={sort} />
                </div>
              </th>
              {assetTypes.map((type) => {
                const count = typeCounts.get(type.id) || 0;
                return (
                  <th
                    key={type.id}
                    className={sort?.key === type.id ? sortableThActive : sortableThInactive}
                    onClick={() => toggleSort(type.id)}
                  >
                    <div className="flex items-center gap-1.5">
                      {type.name}
                      {count > 0 && (
                        <span className="inline-flex h-4 min-w-4 items-center justify-center rounded-full bg-muted px-1 text-[10px] font-medium text-muted-foreground">
                          {count}
                        </span>
                      )}
                      <SortIcon columnKey={type.id} sort={sort} />
                    </div>
                  </th>
                );
              })}
              <th
                className={cn(sort?.key === 'total' ? sortableThActive : sortableThInactive, 'text-center')}
                onClick={() => toggleSort('total')}
              >
                <div className="flex items-center justify-center gap-1">
                  Total
                  <SortIcon columnKey="total" sort={sort} />
                </div>
              </th>
            </tr>
          </thead>
          <tbody>
            {sortedEmployees.map((emp) => {
              // Group assets by type
              const assetsByType = new Map<string, typeof emp.assets>();
              for (const asset of emp.assets) {
                const existing = assetsByType.get(asset.assetType.id) || [];
                existing.push(asset);
                assetsByType.set(asset.assetType.id, existing);
              }

              return (
                <tr
                  key={emp.id}
                  className="cursor-pointer border-b border-border/50 transition-colors hover:bg-muted/20"
                  onClick={() => onRowClick(emp.id)}
                >
                  <td className="px-4 py-3">
                    <EmployeeCell employee={emp} />
                  </td>
                  {assetTypes.map((type) => {
                    const typeAssets = assetsByType.get(type.id);
                    return (
                      <td key={type.id} className="group/cell px-4 py-3 text-sm">
                        <div className="space-y-1">
                          {typeAssets && typeAssets.length > 0 ? (
                            typeAssets.map((a, i) => (
                              <div key={a.id} className="flex items-center gap-1.5">
                                <AssetPopover
                                  asset={a}
                                  employeeName={formatName({ firstName: emp.firstName, lastName: emp.lastName })}
                                />
                                {i === typeAssets.length - 1 && (
                                  <AddAssetPopover
                                    employeeId={emp.id}
                                    assetTypeId={type.id}
                                    assetTypeName={type.name}
                                  />
                                )}
                              </div>
                            ))
                          ) : (
                            <AddAssetPopover
                              employeeId={emp.id}
                              assetTypeId={type.id}
                              assetTypeName={type.name}
                              alwaysVisible
                            />
                          )}
                        </div>
                      </td>
                    );
                  })}
                  <td className="px-4 py-3 text-center">
                    <span
                      className={cn(
                        'inline-flex h-6 min-w-6 items-center justify-center rounded-full px-2 text-xs font-medium',
                        emp.assets.length > 0
                          ? 'bg-primary/10 text-primary'
                          : 'bg-muted text-muted-foreground',
                      )}
                    >
                      {emp.assets.length}
                    </span>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function AccessTable({
  employees,
  accessTypes,
  sort,
  onSort,
  onRowClick,
}: {
  employees: InventoryEmployee[];
  accessTypes: { id: string; name: string; icon: string | null }[];
  sort: SortConfig;
  onSort: (s: SortConfig) => void;
  onRowClick: (id: string) => void;
}) {
  const formatName = useEmployeeNameFormatter();
  const toggleSort = useToggleSort(sort, onSort);

  // Count total grants per type across all employees
  const typeCounts = useMemo(() => {
    const counts = new Map<string, number>();
    for (const emp of employees) {
      for (const grant of emp.accessGrants) {
        counts.set(grant.accessType.id, (counts.get(grant.accessType.id) || 0) + 1);
      }
    }
    return counts;
  }, [employees]);

  const sortedEmployees = useMemo(() => {
    if (!sort) return employees;
    const dir = sort.direction === 'asc' ? 1 : -1;
    const nameKey = (e: InventoryEmployee) =>
      formatName({ firstName: e.firstName, lastName: e.lastName }).toLowerCase();

    return [...employees].sort((a, b) => {
      if (sort.key === 'employee') {
        return nameKey(a).localeCompare(nameKey(b)) * dir;
      }
      if (sort.key === 'total') {
        return (a.accessGrants.length - b.accessGrants.length) * dir;
      }
      const countA = a.accessGrants.filter((g) => g.accessType.id === sort.key).length;
      const countB = b.accessGrants.filter((g) => g.accessType.id === sort.key).length;
      if (countA !== countB) return (countA - countB) * dir;
      return nameKey(a).localeCompare(nameKey(b));
    });
  }, [employees, sort, formatName]);

  return (
    <div className="overflow-hidden rounded-xl border border-border bg-card">
      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b border-border/50 bg-muted/30">
              <th className={sort?.key === 'employee' ? sortableThActive : sortableThInactive} onClick={() => toggleSort('employee')}>
                <div className="flex items-center gap-1">
                  Employee
                  <SortIcon columnKey="employee" sort={sort} />
                </div>
              </th>
              {accessTypes.map((type) => {
                const count = typeCounts.get(type.id) || 0;
                return (
                  <th
                    key={type.id}
                    className={sort?.key === type.id ? sortableThActive : sortableThInactive}
                    onClick={() => toggleSort(type.id)}
                  >
                    <div className="flex items-center gap-1.5">
                      {type.name}
                      {count > 0 && (
                        <span className="inline-flex h-4 min-w-4 items-center justify-center rounded-full bg-muted px-1 text-[10px] font-medium text-muted-foreground">
                          {count}
                        </span>
                      )}
                      <SortIcon columnKey={type.id} sort={sort} />
                    </div>
                  </th>
                );
              })}
              <th
                className={cn(sort?.key === 'total' ? sortableThActive : sortableThInactive, 'text-center')}
                onClick={() => toggleSort('total')}
              >
                <div className="flex items-center justify-center gap-1">
                  Total
                  <SortIcon columnKey="total" sort={sort} />
                </div>
              </th>
            </tr>
          </thead>
          <tbody>
            {sortedEmployees.map((emp) => {
              // Group access grants by type
              const grantsByType = new Map<string, typeof emp.accessGrants>();
              for (const grant of emp.accessGrants) {
                const existing = grantsByType.get(grant.accessType.id) || [];
                existing.push(grant);
                grantsByType.set(grant.accessType.id, existing);
              }

              return (
                <tr
                  key={emp.id}
                  className="cursor-pointer border-b border-border/50 transition-colors hover:bg-muted/20"
                  onClick={() => onRowClick(emp.id)}
                >
                  <td className="px-4 py-3">
                    <EmployeeCell employee={emp} />
                  </td>
                  {accessTypes.map((type) => {
                    const typeGrants = grantsByType.get(type.id);
                    return (
                      <td key={type.id} className="group/cell px-4 py-3 text-sm">
                        <div className="space-y-1">
                          {typeGrants && typeGrants.length > 0 ? (
                            typeGrants.map((g, i) => (
                              <div key={g.id} className="flex items-center gap-1.5">
                                <AccessGrantPopover
                                  grant={g}
                                  typeName={type.name}
                                  employeeName={formatName({ firstName: emp.firstName, lastName: emp.lastName })}
                                />
                                {i === typeGrants.length - 1 && (
                                  <AddAccessGrantPopover
                                    employeeId={emp.id}
                                    accessTypeId={type.id}
                                    accessTypeName={type.name}
                                    defaultEmail={emp.workEmail}
                                  />
                                )}
                              </div>
                            ))
                          ) : (
                            <AddAccessGrantPopover
                              employeeId={emp.id}
                              accessTypeId={type.id}
                              accessTypeName={type.name}
                              defaultEmail={emp.workEmail}
                              alwaysVisible
                            />
                          )}
                        </div>
                      </td>
                    );
                  })}
                  <td className="px-4 py-3 text-center">
                    <span
                      className={cn(
                        'inline-flex h-6 min-w-6 items-center justify-center rounded-full px-2 text-xs font-medium',
                        emp.accessGrants.length > 0
                          ? 'bg-primary/10 text-primary'
                          : 'bg-muted text-muted-foreground',
                      )}
                    >
                      {emp.accessGrants.length}
                    </span>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function EmployeeCell({ employee }: { employee: InventoryEmployee }) {
  const formatName = useEmployeeNameFormatter();
  return (
    <Link
      to="/employees/$id"
      params={{ id: employee.id }}
      onClick={(e) => e.stopPropagation()}
      className="flex items-center gap-3 hover:text-primary"
    >
      <Avatar className="h-8 w-8">
        {employee.avatarUrl && <AvatarImage src={employee.avatarUrl} alt={employee.firstName} />}
        <AvatarFallback className="text-[10px]">
          {getInitials(employee.firstName, employee.lastName)}
        </AvatarFallback>
      </Avatar>
      <div className="min-w-0">
        <div className="truncate text-sm font-medium">
          {formatName({ firstName: employee.firstName, lastName: employee.lastName })}
        </div>
        {employee.department && (
          <div className="truncate text-xs text-muted-foreground">{employee.department.name}</div>
        )}
      </div>
    </Link>
  );
}

function SkeletonTable() {
  return (
    <div className="overflow-hidden rounded-xl border border-border bg-card">
      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b border-border/50 bg-muted/30">
              <th className="px-4 py-3 text-left">
                <Skeleton className="h-3 w-20" />
              </th>
              {Array.from({ length: 4 }).map((_, i) => (
                <th key={i} className="px-4 py-3 text-left">
                  <Skeleton className="h-3 w-16" />
                </th>
              ))}
              <th className="px-4 py-3 text-center">
                <Skeleton className="mx-auto h-3 w-10" />
              </th>
            </tr>
          </thead>
          <tbody>
            {Array.from({ length: 8 }).map((_, i) => (
              <tr key={i} className="border-b border-border/50">
                <td className="px-4 py-3">
                  <div className="flex items-center gap-3">
                    <Skeleton className="h-8 w-8 rounded-md" />
                    <div className="space-y-1.5">
                      <Skeleton className="h-3.5 w-28" />
                      <Skeleton className="h-3 w-20" />
                    </div>
                  </div>
                </td>
                {Array.from({ length: 4 }).map((_, j) => (
                  <td key={j} className="px-4 py-3">
                    <Skeleton className="h-3.5 w-20" />
                  </td>
                ))}
                <td className="px-4 py-3 text-center">
                  <Skeleton className="mx-auto h-6 w-6 rounded-full" />
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function EmptyState() {
  return (
    <div className="flex flex-col items-center justify-center rounded-xl border border-border bg-card py-16">
      <p className="text-sm text-muted-foreground">No data available</p>
    </div>
  );
}
